import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selector, useRecoilValue } from 'recoil'

import { GRAPH_COLORS } from 'consts'
import { dashboardTheme } from 'recoil/atoms'
import { colorsUtils, getRandomColorsArray } from 'utils'

import { EntitiesColorsContext } from './context'

const getDashboardThemeColorsByEntityState = entityType =>
  selector({
    key: `dashboardThemeColorsBy${entityType}State`,
    get: ({ get }) => {
      const dashboardThemeValue = get(dashboardTheme)
      const entities = dashboardThemeValue?.entities?.[entityType] || {}
      const colorsByEntity = Object.entries(entities).map(([entityId, value]) => [entityId, value?.color])

      return new Map(colorsByEntity)
    },
  })
const dashboardThemeColorsByCreatorsState = getDashboardThemeColorsByEntityState('creators')
const dashboardThemeColorsByTopicsState = getDashboardThemeColorsByEntityState('topics')
const dashboardThemeColorsByBrandsState = getDashboardThemeColorsByEntityState('brands')

const getHexRandomColor = () => getRandomColorsArray(1)[0]

export function EntitiesColorsProvider({ children }) {
  const [shouldRerender, setShouldRerender] = useState(false)

  const dashboardThemeColorsByCreators = useRecoilValue(dashboardThemeColorsByCreatorsState)
  const dashboardThemeColorsByTopics = useRecoilValue(dashboardThemeColorsByTopicsState)
  const dashboardThemeColorsByBrands = useRecoilValue(dashboardThemeColorsByBrandsState)

  const colorsByCreators = useRef(dashboardThemeColorsByCreators)
  const colorsByTopics = useRef(dashboardThemeColorsByTopics)
  const colorsByBrands = useRef(dashboardThemeColorsByBrands)
  const colorsByVideos = useRef(new Map())

  const defaultColorsByCreators = useRef(new Set(GRAPH_COLORS))
  const defaultColorsByTopics = useRef(new Set(GRAPH_COLORS))
  const defaultColorsByVideos = useRef(new Set(GRAPH_COLORS))
  const defaultColorsByBrands = useRef(new Set(GRAPH_COLORS))

  const usedColorsSetByCreators = useRef(new Set(colorsByCreators.current.values()))
  const usedColorsSetByTopics = useRef(new Set(colorsByTopics.current.values()))
  const usedColorsSetByVideos = useRef(new Set(colorsByVideos.current.values()))
  const usedColorsSetByBrands = useRef(new Set(colorsByBrands.current.values()))

  const colorSets = useMemo(() => {
    return {
      creators: {
        usedColors: usedColorsSetByCreators,
        defaultColors: defaultColorsByCreators,
        colorsByEntity: colorsByCreators,
      },
      topics: {
        usedColors: usedColorsSetByTopics,
        defaultColors: defaultColorsByTopics,
        colorsByEntity: colorsByTopics,
      },
      videos: {
        usedColors: usedColorsSetByVideos,
        defaultColors: defaultColorsByVideos,
        colorsByEntity: colorsByVideos,
      },
      brands: {
        usedColors: usedColorsSetByBrands,
        defaultColors: defaultColorsByBrands,
        colorsByEntity: colorsByBrands,
      },
    }
  }, [])

  const rerender = () => {
    setShouldRerender(oldState => !oldState)
  }

  const getUniqueColor = entityType => {
    const { usedColors, defaultColors } = colorSets?.[entityType] ?? {}

    if (defaultColors.current.size > 0) {
      const [defaultColor] = defaultColors.current

      const newDefaultColors = new Set(
        [...defaultColors.current].filter(color => {
          return !colorsUtils.isSameColor(color, defaultColor)
        })
      )
      defaultColors.current = newDefaultColors

      usedColors.current = new Set(usedColors.current.add(defaultColor))

      return defaultColor
    }

    const newColor = getHexRandomColor()

    if (usedColors.current.has(newColor)) {
      return getUniqueColor(entityType)
    }

    usedColors.current = new Set(usedColors.current.add(newColor))

    return newColor
  }

  const handleSetDefaultEntityColor = (entityId, entityType) => {
    const defaultColor = getUniqueColor(entityType)
    const colorsByEntity = colorSets?.[entityType]?.colorsByEntity ?? {}

    colorsByEntity.current = new Map(colorsByEntity.current.set(entityId, defaultColor))

    return defaultColor
  }

  const getColorByEntityId = useCallback(
    (entityId, entityType) => {
      const colorsByEntity = colorSets?.[entityType]?.colorsByEntity ?? {}
      const colors = new Map(colorsByEntity.current)
      const hasColor = colors.has(entityId)

      if (hasColor) {
        const currentColor = colors.get(entityId)
        return currentColor
      }

      const defaultColor = handleSetDefaultEntityColor(entityId, entityType)
      return defaultColor
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldRerender]
  )

  const updateColors = useCallback(
    (entityType, dashboardThemeColors) => {
      const { colorsByEntity, usedColors } = colorSets?.[entityType] ?? {}

      const updatedColorsByEntity = new Map([...colorsByEntity.current, ...dashboardThemeColors])
      colorsByEntity.current = updatedColorsByEntity

      const newUsedColors = new Set([...usedColors.current, ...colorsByEntity.current.values()])
      usedColors.current = newUsedColors
    },
    [colorSets]
  )

  useEffect(() => {
    updateColors('creators', dashboardThemeColorsByCreators)
    updateColors('topics', dashboardThemeColorsByTopics)
    updateColors('brands', dashboardThemeColorsByBrands)
    updateColors('videos', colorsByVideos.current)

    rerender()
  }, [dashboardThemeColorsByBrands, dashboardThemeColorsByCreators, dashboardThemeColorsByTopics, updateColors])

  const getColorByEntityIdMemo = useMemo(() => {
    return getColorByEntityId
  }, [getColorByEntityId])

  return (
    <EntitiesColorsContext.Provider
      value={{
        getColorByEntityId: getColorByEntityIdMemo,
      }}
    >
      {children}
    </EntitiesColorsContext.Provider>
  )
}
